import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

type Variant = 'default' | 'danger' | 'primary';
type Size = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Obligatorio por accesibilidad: describe la acción del botón. */
  'aria-label': string;
  icon: ReactNode;
  variant?: Variant;
  size?: Size;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  default: 'text-brand-gray hover:bg-brand-ghost hover:text-brand-dark',
  primary: 'text-brand-tomato hover:bg-brand-tomato/10',
  danger: 'text-red-500 hover:bg-red-50',
};

const SIZE_CLASSES: Record<Size, string> = {
  // Touch targets ≥44px por recomendación WCAG.
  sm: 'h-11 w-11 rounded-lg',
  md: 'h-11 w-11 rounded-lg',
  lg: 'h-12 w-12 rounded-xl',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, variant = 'default', size = 'md', icon, type = 'button', ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-brand-tomato/40',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className
      )}
      {...props}
    >
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
    </button>
  );
});

export default IconButton;
